import { inject, Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { from, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { AnimalLookupDialogComponent } from './animal-lookup-dialog.component';
import { AnimalInfoDialogComponent } from './animal-info-dialog.component';
import { RvoUbnMapDialogComponent } from './rvo-ubn-map-dialog.component';
import { RvoAnimalRouteMapDialogComponent } from './rvo-animal-route-map-dialog.component';
import { RvoVerblijfPlaats } from './rvo-animal.models';
import { formatVerblijfAddress } from './animal-info.utils';

export interface AnimalInfoDialogParams {
  country_code: string;
  identification_number: string;
  animal_species?: string;
}

export interface AnimalLookupDialogParams {
  country_code?: string | null;
  identification_number?: string | null;
  animal_species?: string | null;
}

export interface RvoRouteMapDialogParams {
  places: RvoVerblijfPlaats[];
  dierLandcode: string | null;
  dierLevensnummer: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AnimalLookupDialogService {
  private readonly _modalCtrl = inject(ModalController);

  public openLookup(params?: AnimalLookupDialogParams): Observable<void> {
    return from(
      this._modalCtrl.create({
        component: AnimalLookupDialogComponent,
        componentProps: {
          initialCountry: params?.country_code ?? null,
          initialId: params?.identification_number ?? null,
          initialSpecies: params?.animal_species ?? null,
        },
        cssClass: 'modal-lg-compact'
      })
    ).pipe(
      tap((modal) => modal.present()),
      switchMap((modal) => modal.onDidDismiss()),
      map(() => undefined),
      take(1)
    );
  }

  public openInfo(params: AnimalInfoDialogParams): Observable<void> {
    return from(
      this._modalCtrl.create({
        component: AnimalInfoDialogComponent,
        componentProps: {
          countryCode: params.country_code,
          identificationNumber: params.identification_number,
          animalSpecies: params.animal_species ?? '1',
        },
        cssClass: 'modal-lg'
      })
    ).pipe(
      tap((modal) => modal.present()),
      switchMap((modal) => modal.onDidDismiss()),
      map(() => undefined),
      take(1)
    );
  }

  public openUbnMap(ubn: string, fallbackAddress?: string | null): Observable<void> {
    return from(
      this._modalCtrl.create({
        component: RvoUbnMapDialogComponent,
        componentProps: {
          ubn,
          fallbackAddress: fallbackAddress ?? null
        },
        cssClass: 'modal-ubn-map'
      })
    ).pipe(
      tap((modal) => modal.present()),
      switchMap((modal) => modal.onDidDismiss()),
      map(() => undefined),
      take(1)
    );
  }

  public openRouteMap(params: RvoRouteMapDialogParams): Observable<void> {
    return from(
      this._modalCtrl.create({
        component: RvoAnimalRouteMapDialogComponent,
        componentProps: {
          places: params.places,
          dierLandcode: params.dierLandcode,
          dierLevensnummer: params.dierLevensnummer
        },
        cssClass: 'modal-map-xl'
      })
    ).pipe(
      tap((modal) => modal.present()),
      switchMap((modal) => modal.onDidDismiss()),
      map(() => undefined),
      take(1)
    );
  }

  public openMapFromVerblijf(plaatsOrUbn: RvoVerblijfPlaats | string): Observable<void> {
    if (typeof plaatsOrUbn === 'string') {
      return this.openUbnMap(plaatsOrUbn);
    }

    const ubn = plaatsOrUbn.meldingeenheid?.trim();
    if (!ubn) {
      return from(Promise.resolve()).pipe(map(() => undefined));
    }

    return this.openUbnMap(ubn, formatVerblijfAddress(plaatsOrUbn));
  }
}
